"use client";

import { ReactElement } from "react";
import {
  ShieldCheckIcon,
  UsersIcon,
  HeartIcon,
  ShieldCardIcon,
  MailIcon,
} from "staysure-component-library";

import withBrand from "@/components/with-brand";
import { LeftSectionProps, MenuItem } from "@/types/renewals";

import { useLeftPanelUtils } from "./left-panel-utils";
import Style from "./left-panel.module.css";

function LeftSection(props: Readonly<LeftSectionProps>) {
  const { content, brand = "staysure" } = props;
  const { states, handlers, styles } = useLeftPanelUtils(brand);
  const { isLoading, expandedSections } = states;
  const { handleClick, setIsSelectItemByLeftPanel } = handlers;
  const { dynamicStyle, brandStyleText, brandStyleIcon } = styles;

  const iconMap = (item: MenuItem): ReactElement | undefined => {
    const icons: Record<MenuItem["uniqueId"], ReactElement> = {
      "policy-details": <ShieldCheckIcon />,
      travellers: (
        <UsersIcon
          className={`${
            isLoading
              ? "disabled-users-icon"
              : dynamicStyle(
                  item.uniqueId,
                  "active-users-icon",
                  "default-users-icon"
                )
          } ${brandStyleIcon}`}
        />
      ),
      "medical-conditions": <HeartIcon />,
      "policy-extras": <ShieldCardIcon />,
      "contact-details": <MailIcon />,
    };
    return icons[item.uniqueId];
  };

  return (
    <div className="p-spacing-xs">
      <ul>
        {content.menuItems.map((item) => (
          <li
            key={item.uniqueId}
            className="flex gap-spacing-xs items-center mb-spacing-m"
          >
            <div
              className={`${
                isLoading
                  ? "disabled"
                  : dynamicStyle(item.uniqueId, "active-icon", "default-icon")
              } ${brandStyleIcon}`}
            >
              {iconMap(item)}
            </div>
            <div>
              <button
                type="button"
                onClick={(event) => {
                  if (!isLoading) {
                    handleClick(item.uniqueId)(event);
                    setIsSelectItemByLeftPanel(true);
                  }
                }}
                onKeyDown={(event) => {
                  if (event.key === "Enter" && !isLoading) {
                    handleClick(item.uniqueId)(event);
                    setIsSelectItemByLeftPanel(true);
                  }
                }}
                aria-expanded={expandedSections.includes(item.uniqueId)}
                aria-controls={`${item.uniqueId}-panel`}
                id={item.uniqueId}
                className={`${
                  Style.label
                } focus-visible:outline-interactive-focus-default font-[600] text-2xs leading-[150%] underline underline-offset-[2px] ${
                  isLoading
                    ? "decoration-text-functional-subtle"
                    : dynamicStyle(
                        item.uniqueId,
                        `${brandStyleText.actUnderline}`,
                        `${brandStyleText.defUnderline}`
                      )
                }`}
                disabled={isLoading}
              >
                <span
                  className={`${Style.span} ${
                    isLoading
                      ? "text-text-functional-subtle"
                      : dynamicStyle(
                          item.uniqueId,
                          `${brandStyleText.actColor}`,
                          `${brandStyleText.defColor}`
                        )
                  }`}
                >
                  {item.label}
                </span>
              </button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default withBrand(LeftSection);
